import React, { useState, useEffect } from "react";
import { ChevronUp, ChevronDown, Edit2, Check, X, Plus, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Org-wide T-Shirt sizes manager. Super Admin only.
 * Backed by `tshirt_sizes` array on the organization document.
 */
export const TshirtSizesManager = ({ canEdit }) => {
  const [sizes, setSizes] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchSizes = async () => {
    try {
      const res = await api.get("/organization");
      const list = (res.data.organization?.tshirt_sizes || []).slice();
      list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setSizes(list);
    } catch {
      toast.error("Failed to load t-shirt sizes");
    }
  };

  useEffect(() => { fetchSizes(); }, []);

  const addSize = async () => {
    const label = newLabel.trim();
    if (!label) { toast.error("Enter a label"); return; }
    setBusy(true);
    try {
      await api.post("/organization/tshirt-sizes", { label });
      setNewLabel("");
      await fetchSizes();
      toast.success(`Added '${label}'`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add size");
    } finally { setBusy(false); }
  };

  const startEdit = (s) => { setEditingId(s.size_id); setEditingLabel(s.label); };
  const cancelEdit = () => { setEditingId(null); setEditingLabel(""); };

  const saveEdit = async (size_id) => {
    const label = editingLabel.trim();
    if (!label) { toast.error("Label cannot be blank"); return; }
    setBusy(true);
    try {
      await api.patch(`/organization/tshirt-sizes/${size_id}`, { label });
      cancelEdit();
      await fetchSizes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to rename");
    } finally { setBusy(false); }
  };

  const toggleActive = async (s) => {
    setBusy(true);
    try {
      await api.patch(`/organization/tshirt-sizes/${s.size_id}`, { is_active: !s.is_active });
      await fetchSizes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally { setBusy(false); }
  };

  const move = async (idx, dir) => {
    const next = idx + dir;
    if (next < 0 || next >= sizes.length) return;
    const reordered = sizes.slice();
    [reordered[idx], reordered[next]] = [reordered[next], reordered[idx]];
    setSizes(reordered); // optimistic
    setBusy(true);
    try {
      await api.post("/organization/tshirt-sizes/reorder", {
        ordered_ids: reordered.map((s) => s.size_id),
      });
      await fetchSizes();
    } catch {
      toast.error("Failed to reorder");
      await fetchSizes();
    } finally { setBusy(false); }
  };

  if (!canEdit) {
    return (
      <p className="text-xs text-slate-500">
        Only Super Admin can manage T-Shirt sizes. Currently configured:{" "}
        {sizes.filter((s) => s.is_active).map((s) => s.label).join(", ") || "none"}.
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="tshirt-sizes-manager">
      <p className="text-xs text-slate-500">
        Configure the dropdown that appears on Student Profiles. Drag is via the up/down buttons.
        Deactivated sizes are hidden for new selection but remain on existing student records.
      </p>

      {sizes.length === 0 ? (
        <p className="text-sm text-slate-400 italic">No sizes configured yet — add your first below.</p>
      ) : (
        <ul className="space-y-1.5" data-testid="tshirt-sizes-list">
          {sizes.map((s, idx) => {
            const isEditing = editingId === s.size_id;
            return (
              <li
                key={s.size_id}
                data-testid={`tshirt-size-row-${s.size_id}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${s.is_active ? "bg-white border-slate-200" : "bg-slate-50 border-slate-200 opacity-70"}`}
              >
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    disabled={busy || idx === 0}
                    onClick={() => move(idx, -1)}
                    className="p-0.5 text-slate-400 hover:text-violet-600 disabled:opacity-30"
                    data-testid={`tshirt-move-up-${idx}`}
                    aria-label="Move up"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    disabled={busy || idx === sizes.length - 1}
                    onClick={() => move(idx, 1)}
                    className="p-0.5 text-slate-400 hover:text-violet-600 disabled:opacity-30"
                    data-testid={`tshirt-move-down-${idx}`}
                    aria-label="Move down"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>

                {isEditing ? (
                  <Input
                    autoFocus
                    value={editingLabel}
                    onChange={(e) => setEditingLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEdit(s.size_id);
                      if (e.key === "Escape") cancelEdit();
                    }}
                    className="h-8 rounded-md text-sm flex-1"
                    data-testid={`tshirt-edit-input-${s.size_id}`}
                  />
                ) : (
                  <span
                    className={`flex-1 text-sm font-medium ${s.is_active ? "text-slate-900" : "text-slate-500 italic"}`}
                  >
                    {s.label}
                    {!s.is_active && <span className="ml-2 text-[10px] text-slate-400">(Inactive)</span>}
                  </span>
                )}

                {isEditing ? (
                  <>
                    <Button
                      type="button" size="sm" variant="ghost"
                      onClick={() => saveEdit(s.size_id)}
                      className="h-7 px-2"
                      data-testid={`tshirt-save-${s.size_id}`}
                    >
                      <Check className="w-4 h-4 text-green-600" />
                    </Button>
                    <Button
                      type="button" size="sm" variant="ghost" onClick={cancelEdit} className="h-7 px-2"
                    >
                      <X className="w-4 h-4 text-slate-400" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="button" size="sm" variant="ghost"
                      onClick={() => startEdit(s)}
                      className="h-7 px-2"
                      data-testid={`tshirt-edit-${s.size_id}`}
                      title="Rename"
                    >
                      <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                    </Button>
                    <Button
                      type="button" size="sm" variant="ghost"
                      onClick={() => toggleActive(s)}
                      className="h-7 px-2"
                      data-testid={`tshirt-toggle-active-${s.size_id}`}
                      title={s.is_active ? "Deactivate" : "Reactivate"}
                    >
                      {s.is_active
                        ? <Eye className="w-3.5 h-3.5 text-emerald-600" />
                        : <EyeOff className="w-3.5 h-3.5 text-slate-400" />}
                    </Button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex gap-2 pt-2 border-t border-slate-100">
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addSize(); }}
          placeholder="e.g. M, XL, 7-8Y"
          className="h-9 rounded-md text-sm"
          data-testid="tshirt-new-label-input"
        />
        <Button
          type="button" size="sm" onClick={addSize}
          disabled={busy || !newLabel.trim()}
          data-testid="tshirt-add-button"
          className="h-9 px-3 bg-violet-600 hover:bg-violet-700"
        >
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
};

export default TshirtSizesManager;
